﻿import React from "react";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/system";
import PropTypes from "prop-types";
import ShepTabs from "./ShepTabs";
import "./ShepTab.css";

const tabStyles = {
  primary: {
    default: {
      height: "78px",
      width: "300px",
      borderRadius: "10px 10px 0 0",
      backgroundColor: "var(--default-light)",
      border: "1px solid var(--grey-lowlight)",
      fontColor: "var(--dark-border)",
      margin: "0 2px 0 0",
      transition: "all 0.2s ease",
      "& .MuiTab-iconWrapper path": {
        display: "inline-flex",
        fill: "var(--dark-border)",
      },
    },
    hover: {
      backgroundColor: "var( --secondary-10)",
      border: "1px solid var( --secondary-10)",
      fontColor: "var(--primary-main)",
      "& .MuiTab-iconWrapper path": {
        display: "inline-flex",
        fill: "var(--primary-main)",
      },
    },
    selected: {
      backgroundColor: "var(--primary-main)",
      border: "1px solid var(--primary-main)",
      color: "var(--default-light)",
      "& .MuiTab-iconWrapper path": {
        display: "inline-flex",
        fill: "var(--default-light)",
      },
    },
    disabled: {
      backgroundColor: "var(--disabled)",
      border: "1px solid var(--grey-lowlight)",
      fontColor: "var(--grey-lowlight)",
    },
  },
  secondary: {
    default: {
      height: "78px",
      width: "200px",
      color: "var(--neutral-6)",
      //backgroundColor: "var(--default-light)",
      fontWeight: "normal",
      transition: "all 0.2s ease",
      "& .MuiTab-iconWrapper path": {
        display: "inline-flex",
        fill: "var(--neutral-6)",
        fontSize: "15px",
        paddingRight: "15px",
      },
    },
    hover: {
      color: "var(--primary-main)",
    },
    selected: {
      color: "var(--primary-main)",
      fontWeight: "bold",
      "& .MuiTab-iconWrapper path": {
        display: "inline-flex",
        fill: "var(--primary-main)",
        fontSize: "15px",
        paddingRight: "15px",
      },
    },
    disabled: {
      color: "var(--grey-light)",
      fontWeight: "normal",
      "& .MuiTab-iconWrapper path": {
        display: "inline-flex",
        fill: "var(--grey-light)",
        fontSize: "15px",
        paddingRight: "15px",
      },
    },
  },
  tertiary: {
    default: {
      height: "78px",
      width: "200px",
      color: "var(--neutral-6)",
      //backgroundColor: "var(--default-light)",
      fontWeight: "normal",
      transition: "all 0.2s ease",
    },
    hover: {
      color: "var(--primary-main)",
    },
    selected: {
      color: "var(--primary-main)",
      fontWeight: "bold",
    },
    disabled: {
      color: "var(--grey-light)",
      fontWeight: "normal",
    },
  },
  viewData: {
    default: {
      height: "70px",
      flex: 1,
      color: "var(--neutral-6)",
      fontWeight: "normal",
      transition: "all 0.2s ease",
    },
    hover: {
      color: "var(--primary-main)",
    },
    selected: {
      color: "var(--primary-main)",
      fontWeight: "bold",
    },
    disabled: {
      color: "var(--grey-light)",
      fontWeight: "normal",
    },
  },
  gridSelector: {
    default: {
      height: "40px",
      flex: 1,
      color: "var(--neutral-6)",
      fontWeight: "normal",
      transition: "all 0.2s ease",
      padding: "40px",
    },
    hover: {},
    selected: {
      fontWeight: "bold",
    },
    disabled: {
      color: "var(--grey-light)",
      fontWeight: "normal",
    },
  },
};

const getTabStyles = (variant) => {
  return tabStyles[variant];
};

const StyledTab = styled(Tab, {
  shouldForwardProp: (prop) => prop !== "variant",
})(({ variant }) => {
  const styles = getTabStyles(variant);
  return {
    boxSizing: "border-box",
    textTransform: "none",
    fontFamily: "var(--font-family)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    ...styles.default,
    "&:hover": styles.hover,
    "&.Mui-selected": styles.selected,
    "&.Mui-disabled": styles.disabled,
    "& .MuiTab-iconWrapper": {
      ...(variant === "secondary" && {
        fontSize: "15px",
        paddingRight: "15px",
      }),
    },
  };
});

const ShepTab = ({ variant = 'primary', icon, label, ...props }) => {
  const styles = getTabStyles(variant);
  const typographyVariant =
    variant === "primary"
      ? "primary-sheptab"
      : variant === "secondary"
      ? "secondary-sheptab"
      : variant === "viewData"
      ? "view-data-sheptab"
      : variant === "gridSelector"
      ? "grid-selector-sheptab"
      : "tertiary-sheptab";
  return (
    <StyledTab
      variant={variant}
      icon={icon}
      iconPosition="start"
      label={<Typography variant={typographyVariant}>{label}</Typography>}
      sx={{
        "&.MuiTab-root .MuiTab-iconWrapper path": {
          color: styles.default.color,
          fill: styles.default.color,
        },
        "&.MuiTab-root:hover .MuiTab-iconWrapper path": {
          color: styles.hover.color,
          fill: styles.hover.color,
        },
        "&.MuiTab-root.Mui-selected .MuiTab-iconWrapper path": {
          color: styles.selected.color,
          fill: styles.selected.color,
        },
        "&.MuiTab-root.Mui-disabled .MuiTab-iconWrapper path": {
          color: styles.disabled.color,
          fill: styles.disabled.color,
        },
      }}
      {...props}
    />
  );
};

ShepTab.propTypes = {
  variant: PropTypes.oneOf([
    "primary",
    "secondary",
    "tertiary",
    "viewData",
    "gridSelector",
  ]),
  icon: PropTypes.element,
  label: PropTypes.node.isRequired,
};

ShepTab.defaultProps = {
  variant: "primary",
  icon: null,
};

export { ShepTab, ShepTabs };
